import { LoggerService } from '../services/logger/Logger.service';

const logger = LoggerService.getLogger();

// lodash uniq: nullish input -> []
export const uniq = <T>(array: readonly T[] | null | undefined): T[] => (array == null ? [] : [ ...new Set(array) ]);

// SameValueZero: like ===, but treats NaN as equal to NaN (and +0 equal to -0).
const sameValueZero = (a: unknown, b: unknown): boolean => (
  a === b || (a !== a && b !== b)
);

/**
 * lodash-compatible `isEqual`: deep structural equality for primitives (NaN-aware), arrays,
 * plain objects, Date, RegExp, Map and Set. Functions/other exotic values compare by reference.
 */
export const isEqual = (a: unknown, b: unknown): boolean => {
  if (sameValueZero(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a instanceof RegExp && b instanceof RegExp) return a.source === b.source && a.flags === b.flags;

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;
  if (aIsArray && bIsArray) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    return [ ...a.entries() ].every(([ key, val ]) => b.has(key) && isEqual(val, b.get(key)));
  }

  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    return [ ...a ].every(val => b.has(val));
  }

  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => (
    Object.prototype.hasOwnProperty.call(b, key)
    && isEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  ));
};

// lodash clone (shallow): arrays and plain objects are shallow-copied; everything else returned as-is
export const clone = <T>(value: T): T => {
  if (Array.isArray(value)) return [ ...value ] as unknown as T;
  if (value !== null && typeof value === 'object') return { ...(value as object) } as T;
  return value;
};

// lodash clamp(number, lower, upper)
export const clamp = (number: number, lower: number, upper: number): number => Math.min(Math.max(number, lower), upper);

// Fail loud and early on pathological input (deep nesting / lazy Proxy) instead of a raw stack overflow.
const CLONE_DEEP_MAX_DEPTH = 1000;

/**
 * lodash-compatible `cloneDeep`. Recursively clones arrays, plain objects, class instances
 * (prototype preserved, e.g. moment), Date, RegExp, Map and Set. Circular and shared references
 * are preserved via a WeakMap. Primitives and functions are returned by reference.
 * If nesting exceeds CLONE_DEEP_MAX_DEPTH (guards against cyclic-via-getter / Proxy inputs), logs an
 * error and returns the offending node by reference instead of descending further.
 */
export const cloneDeep = <T>(value: T, seen: WeakMap<object, unknown> = new WeakMap(), depth = 0): T => {
  if (value === null || typeof value !== 'object') return value;

  if (depth > CLONE_DEEP_MAX_DEPTH) {
    logger.error(`[cloneDeep] max depth of ${CLONE_DEEP_MAX_DEPTH} exceeded — possible cyclic or pathological input`);
    return value;
  }

  const asObject = value as unknown as object;
  if (seen.has(asObject)) return seen.get(asObject) as T;

  if (value instanceof Date) return new Date(value.getTime()) as unknown as T;
  if (value instanceof RegExp) return new RegExp(value.source, value.flags) as unknown as T;

  const nextDepth = depth + 1;

  if (Array.isArray(value)) {
    const result: unknown[] = [];
    seen.set(asObject, result);
    value.forEach((item, index) => { result[index] = cloneDeep(item, seen, nextDepth); });
    return result as unknown as T;
  }

  if (value instanceof Map) {
    const result = new Map();
    seen.set(asObject, result);
    value.forEach((val, key) => result.set(cloneDeep(key, seen, nextDepth), cloneDeep(val, seen, nextDepth)));
    return result as unknown as T;
  }

  if (value instanceof Set) {
    const result = new Set();
    seen.set(asObject, result);
    value.forEach(val => result.add(cloneDeep(val, seen, nextDepth)));
    return result as unknown as T;
  }

  // Generic object / class instance: preserve the prototype, clone own enumerable keys.
  const result = Object.create(Object.getPrototypeOf(value));
  seen.set(asObject, result);
  Object.keys(asObject).forEach(key => {
    result[key] = cloneDeep((asObject as Record<string, unknown>)[key], seen, nextDepth);
  });
  return result as T;
};

/**
 * lodash-compatible `random`, backed by Math.random().
 * - `random()` / `random(true)` -> [0, 1] (float when `true`, otherwise 0 or 1)
 * - `random(upper)` -> [0, upper]
 * - `random(lower, upper)` -> [lower, upper], inclusive of `upper`
 * - A boolean in place of `upper`/`floating`, or fractional bounds, forces a float result.
 */
export const random = (
  lowerArg?: number | boolean,
  upperArg?: number | boolean,
  floatingArg?: boolean,
): number => {
  let floating = floatingArg;
  if (floating === undefined) {
    if (typeof upperArg === 'boolean') floating = upperArg;
    else if (typeof lowerArg === 'boolean') floating = lowerArg;
  }

  let lower = typeof lowerArg === 'number' ? lowerArg : undefined;
  let upper = typeof upperArg === 'number' ? upperArg : undefined;

  if (lower === undefined && upper === undefined) {
    lower = 0;
    upper = 1;
  } else if (upper === undefined) {
    upper = lower as number;
    lower = 0;
  } else if (lower === undefined) {
    lower = 0;
  }

  if (lower > upper) {
    const temp = lower;
    lower = upper;
    upper = temp;
  }

  if (floating || lower % 1 !== 0 || upper % 1 !== 0) {
    return lower + Math.random() * (upper - lower);
  }
  return lower + Math.floor(Math.random() * (upper - lower + 1));
};

// An iteratee is either a function or a property name (lodash "_.property" shorthand).
type Iteratee<T> = ((item: T) => unknown) | PropertyKey;

const toIterateeFn = <T>(iteratee: Iteratee<T>): ((item: T) => unknown) => (
  typeof iteratee === 'function' ? iteratee : (item: T) => (item as any)?.[iteratee]
);

// lodash uniqBy: keeps the first element for each distinct iteratee result; nullish input -> []
export const uniqBy = <T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): T[] => {
  if (array == null) return [];
  const fn = toIterateeFn(iteratee);
  const seen = new Set<unknown>();
  const result: T[] = [];
  array.forEach(item => {
    const key = fn(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  });
  return result;
};

// Shared ranking core for maxBy/minBy. Skips null/undefined/NaN criteria and keeps the first extremum (lodash behavior).
const extremumBy = <T>(
  array: readonly T[] | null | undefined,
  iteratee: Iteratee<T>,
  isBetter: (candidate: number, current: number) => boolean,
): T | undefined => {
  if (array == null || array.length === 0) return undefined;
  const fn = toIterateeFn(iteratee);
  let result: T | undefined;
  let computed: number | undefined;
  array.forEach(item => {
    const value = fn(item) as number;
    if (value != null && !Number.isNaN(value) && (computed === undefined || isBetter(value, computed))) {
      computed = value;
      result = item;
    }
  });
  return result;
};

// lodash maxBy: element with the greatest iteratee value; undefined for empty/nullish input
export const maxBy = <T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): T | undefined => (
  extremumBy(array, iteratee, (candidate, current) => candidate > current)
);

// lodash minBy: element with the smallest iteratee value; undefined for empty/nullish input
export const minBy = <T>(array: readonly T[] | null | undefined, iteratee: Iteratee<T>): T | undefined => (
  extremumBy(array, iteratee, (candidate, current) => candidate < current)
);
